import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
} from "@material-ui/core";
import PropTypes from "prop-types";
import useApi from "../hooks/useApi";
import { useUserContext } from "../contexts/userContext";
import { useStoreContext } from "../contexts/storeContext";
import moment from "moment";
import { DatePicker } from "antd";
import {
  showSuccessToast,
  SUCCESS_TOAST_MESSAGES,
} from "../utils/showSuccessToast";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "8px",
      padding: 0,
      minWidth: "600px",
      maxWidth: "600px",
      backgroundColor: "#FFFFFF",
      border: "1px solid #CED4DA",
    },
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  },
  dialogTitle: {
    padding: theme.spacing(2.5, 3),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #CED4DA",
    margin: 0,
  },
  title: {
    fontFamily: "Inter, sans-serif",
    fontSize: "20px",
    fontWeight: 600,
    color: "#212529",
    lineHeight: "28px",
    margin: 0,
  },
  closeButton: {
    padding: theme.spacing(0.5),
    color: "#495057",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  closeIcon: {
    width: 20,
    height: 20,
  },
  dialogContent: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: "1px solid #CED4DA",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  label: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#212529",
    marginBottom: theme.spacing(0.75),
    display: "block",
  },
  tableContainer: {
    marginTop: theme.spacing(1),
    border: "1px solid #DEE2E6",
    borderRadius: "8px",
    overflow: "hidden",
  },
  table: {
    width: "100%",
  },
  tableHeader: {
    backgroundColor: "#E4DAFF",
    "& .MuiTableCell-head": {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      fontWeight: 600,
      color: "#212529",
      lineHeight: "20px",
      padding: theme.spacing(1.5, 2),
      borderBottom: "1px solid #DEE2E6",
    },
  },
  sortIcon: {
    width: 12,
    height: 12,
    marginLeft: 4,
    color: "#495057",
  },
  tableRow: {
    backgroundColor: "#FFFFFF",
    "&:nth-of-type(even)": {
      backgroundColor: "#FFFFFF",
    },
    "& .MuiTableCell-body": {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      fontWeight: 400,
      color: "#212529",
      lineHeight: "20px",
      padding: theme.spacing(1.5, 2),
      borderBottom: "1px solid #D5E0EB",
    },
  },
  removeLink: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#DC3545",
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  warningAlert: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: "#FFF3CD",
    border: "1px solid #FFC107",
    borderRadius: "6px",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  warningIcon: {
    width: 20,
    height: 20,
    color: "#FFC107",
    flexShrink: 0,
  },
  warningText: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#856404",
    lineHeight: "20px",
  },
  errorAlert: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    border: "1px solid #E9ECEF",
    borderRadius: "6px",
    backgroundColor: "#FFFFFF",
    padding: theme.spacing(1.25, 2),
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 4,
      backgroundColor: "#DC3545",
      borderRadius: "6px 0 0 6px",
    },
  },
  errorIcon: {
    width: 16,
    height: 16,
    color: "#DC3545",
    flexShrink: 0,
  },
  errorText: {
    color: "#343A40",
    fontFamily: "Inter, sans-serif",
    fontSize: "11px",
    fontWeight: 400,
  },
  pickupSectionTitle: {
    fontFamily: "Inter, sans-serif",
    fontSize: "18px",
    fontWeight: 600,
    color: "#343A40",
    lineHeight: "100%",
    letterSpacing: "0px",
    marginTop: "41px",
  },
  pickupLabel: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#676C76",
    lineHeight: "100%",
    letterSpacing: "2%",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  pickupPickerWrapper: {
    position: "relative",
    display: "inline-block",
  },
  pickupDisplay: {
    border: "1px solid #6F42C1",
    borderRadius: "6px",
    padding: theme.spacing(1.25, 1.5),
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    color: "#343A40",
    pointerEvents: "none",
  },
  pickupMeta: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  pickupDivider: {
    color: "#9AA0AB",
    margin: theme.spacing(0, 0.5),
  },
  pickupMuted: {
    color: "#9AA0AB",
  },
  pickupPicker: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    opacity: 0,
    cursor: "pointer",
    zIndex: 2,
    pointerEvents: "auto",
  },
  pickupPickerInput: {
    height: "100%",
  },
  pickupIcon: {
    width: 16,
    height: 16,
    color: "#6C757D",
    flexShrink: 0,
  },
  instructionsLabel: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#212529",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  instructionsInput: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      color: "#212529",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#CED4DA",
        borderRadius: "6px",
      },
      "&:hover fieldset": {
        borderColor: "#ADB5BD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6F42C1",
        borderWidth: "1px",
      },
    },
    "& .MuiInputBase-input": {
      fontFamily: "Inter, sans-serif",
      fontWeight: 400,
      fontStyle: "normal",
      fontSize: "14px",
      lineHeight: "100%",
      letterSpacing: "0px",
      padding: theme.spacing(1.25, 1.5),
      "&::placeholder": {
        color: "#ADB5BD",
        opacity: 1,
        fontFamily: "Inter, sans-serif",
        fontWeight: 400,
        fontStyle: "normal",
        fontSize: "14px",
        lineHeight: "100%",
        letterSpacing: "0px",
      },
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    paddingTop: theme.spacing(1.5),
    justifyContent: "flex-start",
    gap: theme.spacing(1.5),
    margin: 0,
  },
  cancelButton: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    padding: theme.spacing(1, 2),
    borderRadius: "6px",
    textTransform: "none",
    border: "1px solid #A78BFA",
    color: "#A78BFA",
    backgroundColor: "#FFFFFF",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "#F8F9FA",
      borderColor: "#A78BFA",
    },
  },
  createButton: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    padding: theme.spacing(1, 2),
    borderRadius: "6px",
    textTransform: "none",
    backgroundColor: "#7650E1",
    color: "#FFFFFF",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "#5A32A3",
    },
    "&:disabled": {
      backgroundColor: "#E1E1E1",
      color: "#8F8F8F",
    },
  },
}));

/**
 * CreateShipmentModal Component
 *
 * Modal dialog for creating a new shipment
 *
 * @param {boolean} open - Whether the modal is open
 * @param {Function} onClose - Handler for closing the modal
 * @param {Function} onCreateShipment - Handler for creating the shipment
 * @param {Array} selectedDevices - Array of selected devices to include in shipment
 */
const CreateShipmentModal = ({
  open,
  onClose,
  onCreateShipment,
  selectedDevices = [],
  boxInventory = [],
}) => {
  const classes = useStyles();
  const { request } = useApi();
  const { user } = useUserContext();
  const [boxType, setBoxType] = useState("");
  const [devices, setDevices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pickupRange, setPickupRange] = useState([]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(moment());
  const [instructions, setInstructions] = useState("");
  const [pickerErrorMessage, setPickerErrorMessage] = useState("");
  
  // Sync devices with selectedDevices prop when modal opens or selectedDevices changes
  useEffect(() => {
    if (open && selectedDevices) {
      setDevices(selectedDevices);
    }
  }, [open, selectedDevices]);
  
  // Check if selected box type has zero or negative inventory
  const getBoxInventoryWarning = () => {
    if (!boxType || !boxInventory || boxInventory.length === 0) return null;
    
    // Find the inventory item for the selected box type
    const inventoryItem = boxInventory.find((item) => {
      // Match box type - handle both "A", "B", "C" and "Type A", "Type B", "Type C"
      const itemType = item.type || "";
      const normalizedItemType = itemType.replace("Type ", "").trim().toUpperCase();
      const normalizedBoxType = boxType.toUpperCase();
      return normalizedItemType === normalizedBoxType;
    });
    
    if (!inventoryItem) return null;
    
    // Display 0 if quantity is negative, otherwise use actual quantity
    const displayQuantity = inventoryItem.current < 0 ? 0 : inventoryItem.current;
    
    // Show warning if quantity is 0 or negative
    if (displayQuantity <= 0) {
      return `Warning: Box Type ${boxType} inventory is 0. Please ensure you have sufficient inventory before creating a shipment.`;
    }
    
    return null;
  };
  
  const boxInventoryWarning = getBoxInventoryWarning();
  // Helper function to calculate minimum time based on current time
  // If current time is :00-:45, min time is next hour at :00
  // If current time is :46-:59, min time is 2 hours from now at :00
  const getMinTime = () => {
    const now = moment();
    const currentMinute = now.minute();
    const currentHour = now.hour();
    
    let hoursToAdd = 1; // Default: next hour
    if (currentMinute >= 46) {
      hoursToAdd = 2; // If :46-:59, add 2 hours
    }
    
    // Calculate the target time (next hour or 2 hours from now, at :00)
    const targetTime = moment(now).add(hoursToAdd, "hours").minute(0).second(0).millisecond(0);
    
    const hour24 = targetTime.hour(); // 0-23 format (0 = midnight, 12 = noon, 13-23 = 1PM-11PM)
    const minute = 0; // Always :00
    let hour = hour24;
    let ampm = "AM";
    
    // Convert 24-hour to 12-hour format
    if (hour24 === 0) {
      hour = 12;
      ampm = "AM"; // Midnight (0:00)
    } else if (hour24 === 12) {
      hour = 12;
      ampm = "PM"; // Noon (12:00)
    } else if (hour24 > 12) {
      hour = hour24 - 12;
      ampm = "PM"; // 1PM-11PM (13:00-23:00)
    } else {
      hour = hour24;
      ampm = "AM"; // 1AM-11AM (1:00-11:00)
    }
    
    // Debug logging
    console.log("getMinTime debug:", {
      currentTime: now.format("YYYY-MM-DD HH:mm:ss"),
      currentHour,
      currentMinute,
      hoursToAdd,
      targetTime: targetTime.format("YYYY-MM-DD HH:mm:ss"),
      hour24,
      result: { hour, minute, ampm }
    });
    
    return { hour, minute, ampm };
  };

  // Get minimum "to time" based on "from time" and "from date" (24 hours after)
  const getMinToTime = () => {
    if (!pickupRange || pickupRange.length === 0 || !pickupRange[0] || !fromTime || 
        fromTime.hour === "" || fromTime.minute === "" || 
        fromTime.hour === undefined || fromTime.minute === undefined ||
        fromTime.ampm === undefined || fromTime.ampm === "") {
      return null;
    }
    
    // Convert fromTime to 24-hour format
    const fromHour = typeof fromTime.hour === "number" ? fromTime.hour : parseInt(String(fromTime.hour), 10);
    let fromHour24 = fromHour;
    if (fromTime.ampm === "PM") {
      if (fromHour === 12) {
        fromHour24 = 12;
      } else {
        fromHour24 = fromHour + 12;
      }
    } else {
      if (fromHour === 12) {
        fromHour24 = 0;
      } else {
        fromHour24 = fromHour;
      }
    }
    
    const fromMinute = typeof fromTime.minute === "number" ? fromTime.minute : parseInt(String(fromTime.minute), 10);
    
    // Create moment with from date and from time
    const fromDateTime = moment(pickupRange[0])
      .hour(fromHour24)
      .minute(fromMinute)
      .second(0)
      .millisecond(0);
    
    // Add 24 hours
    const minToDateTime = moment(fromDateTime).add(24, "hours");
    
    // Convert to 12-hour format
    const hour24 = minToDateTime.hour();
    const minute = minToDateTime.minute();
    let hour = hour24;
    let ampm = "AM";
    
    if (hour24 === 0) {
      hour = 12;
      ampm = "AM";
    } else if (hour24 === 12) {
      hour = 12;
      ampm = "PM";
    } else if (hour24 > 12) {
      hour = hour24 - 12;
      ampm = "PM";
    } else {
      hour = hour24;
      ampm = "AM";
    }
    
    return { hour, minute, ampm };
  };

  // Check if a time is below the minimum
  const isTimeBelowMinimum = (time) => {
    if (!time || time.hour === "" || time.minute === "" || 
        time.hour === undefined || time.minute === undefined ||
        time.ampm === undefined || time.ampm === "") {
      return false;
    }
    
    // Convert hour and minute to numbers
    const timeHour = typeof time.hour === "number" ? time.hour : parseInt(String(time.hour), 10);
    const timeMinute = typeof time.minute === "number" ? time.minute : parseInt(String(time.minute), 10);
    
    if (isNaN(timeHour) || isNaN(timeMinute)) {
      return false;
    }
    
    const minTime = getMinTime();
    const minHour = minTime.hour;
    const minAMPM = minTime.ampm;
    const minMinute = minTime.minute;
    
    // Convert to 24-hour format for comparison
    let timeHour24 = timeHour;
    if (time.ampm === "PM") {
      if (timeHour === 12) {
        timeHour24 = 12; // 12 PM = noon (12:00)
      } else {
        timeHour24 = timeHour + 12; // 1-11 PM = 13-23
      }
    } else { // AM
      if (timeHour === 12) {
        timeHour24 = 0; // 12 AM = midnight (0:00)
      } else {
        timeHour24 = timeHour; // 1-11 AM = 1-11
      }
    }
    
    let minHour24 = minHour;
    if (minAMPM === "PM") {
      if (minHour === 12) {
        minHour24 = 12; // 12 PM = noon (12:00)
      } else {
        minHour24 = minHour + 12; // 1-11 PM = 13-23
      }
    } else { // AM
      if (minHour === 12) {
        minHour24 = 0; // 12 AM = midnight (0:00)
      } else {
        minHour24 = minHour; // 1-11 AM = 1-11
      }
    }
    
    // Special case: If minimum is midnight (0), it means the minimum is at the start of the next day
    // Any time on the current day (1-23) is before midnight of the next day, so it's below minimum
    // Only midnight itself (0) or times after midnight would be valid
    if (minHour24 === 0) {
      // If time is also midnight, check minutes
      if (timeHour24 === 0) {
        return timeMinute < minMinute;
      }
      // Any other time (1-23) is before midnight, so it's below minimum
      return true;
    }
    
    // Compare hours first, then minutes if hours are equal
    if (timeHour24 < minHour24) {
      return true;
    } else if (timeHour24 === minHour24) {
      return timeMinute < minMinute;
    }
    return false;
  };

  const [fromTime, setFromTime] = useState(() => {
    return getMinTime();
  });
  const [toTime, setToTime] = useState(() => {
    return getMinTime(); // Same as fromTime initially
  });
  const allowCloseRef = useRef(false);

  // Update devices and defaults when modal opens
  useEffect(() => {
    if (!open) return;
    setDevices(selectedDevices);
    setBoxType((current) => current || "A");
    setErrorMessage("");
    setPickerErrorMessage("");
    // Initialize picker month to current month or selected date's month
    if (pickupRange && pickupRange.length > 0 && pickupRange[0]) {
      setPickerMonth(moment(pickupRange[0]));
    } else {
      setPickerMonth(moment());
    }
    // Reset times: from time = min time (next hour if :00-:45, 2 hours if :46-:59), to time = same as from time
    const minTime = getMinTime();
    setFromTime(minTime);
    setToTime({ ...minTime }); // Same as fromTime initially
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDevices, pickupRange]);

  const handleClose = () => {
    setBoxType("");
    setDevices([]);
    setErrorMessage("");
    setPickupRange([]);
    setIsPickerOpen(false);
    setInstructions("");
    setPickerErrorMessage("");
    const minTime = getMinTime();
    setFromTime(minTime);
    setToTime({ ...minTime }); // Same as fromTime
    onClose();
  };

  // Time picker helper functions
  const adjustTime = (time, field, delta, setTime, isFromTime = false) => {
    const newTime = { ...time };
    
    if (field === "hour") {
      let newHour = newTime.hour + delta;
      
      // Prevent going below minimum for "from time"
      if (isFromTime && delta < 0) {
        const minTime = getMinTime();
        const minHour = minTime.hour;
        const minAMPM = minTime.ampm;
        
        // Convert to 24-hour format for comparison
        const currentHour = typeof newTime.hour === "number" ? newTime.hour : parseInt(String(newTime.hour), 10);
        let currentHour24 = currentHour;
        if (newTime.ampm === "PM") {
          if (currentHour === 12) {
            currentHour24 = 12;
          } else {
            currentHour24 = currentHour + 12;
          }
        } else {
          if (currentHour === 12) {
            currentHour24 = 0;
          } else {
            currentHour24 = currentHour;
          }
        }
        
        let minHour24 = minHour;
        if (minAMPM === "PM") {
          if (minHour === 12) {
            minHour24 = 12;
          } else {
            minHour24 = minHour + 12;
          }
        } else {
          if (minHour === 12) {
            minHour24 = 0;
          } else {
            minHour24 = minHour;
          }
        }
        
        // If at or below minimum, don't allow decreasing
        if (currentHour24 <= minHour24 && newTime.ampm === minAMPM) {
          return; // Don't allow going below minimum
        }
      }
      
      if (newHour < 1) newHour = 12;
      if (newHour > 12) newHour = 1;
      newTime.hour = newHour;
      
      // After changing hour, validate against minimum for "from time" only
      if (isFromTime && isTimeBelowMinimum(newTime)) {
        const minTime = getMinTime();
        newTime.hour = minTime.hour;
        newTime.minute = minTime.minute;
        newTime.ampm = minTime.ampm;
      }
    } else if (field === "minute") {
      let newMinute = newTime.minute + delta;
      if (newMinute < 0) newMinute = 59;
      if (newMinute > 59) newMinute = 0;
      newTime.minute = newMinute;
      
      // After changing minute, validate against minimum for "from time" only
      if (isFromTime && isTimeBelowMinimum(newTime)) {
        const minTime = getMinTime();
        newTime.hour = minTime.hour;
        newTime.minute = minTime.minute;
        newTime.ampm = minTime.ampm;
      }
    } else if (field === "ampm") {
      newTime.ampm = newTime.ampm === "AM" ? "PM" : "AM";
      
      // After changing AM/PM, validate against minimum for "from time" only
      if (isFromTime && isTimeBelowMinimum(newTime)) {
        const minTime = getMinTime();
        newTime.hour = minTime.hour;
        newTime.minute = minTime.minute;
        newTime.ampm = minTime.ampm;
      }
    }
    
    setTime(newTime);
  };

  // Handle time input changes
  const handleTimeInputChange = (time, field, value, setTime, isFromTime = false) => {
    const newTime = { ...time };
    
    if (field === "hour") {
      if (value === "") {
        newTime.hour = "";
        setTime(newTime);
        return;
      }
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        if (numValue >= 1 && numValue <= 12) {
          newTime.hour = numValue;
          
          // Validate against minimum for "from time" only
          if (isFromTime && isTimeBelowMinimum(newTime)) {
            const minTime = getMinTime();
            newTime.hour = minTime.hour;
            newTime.minute = minTime.minute;
            newTime.ampm = minTime.ampm;
          }
        } else if (numValue > 12) {
          newTime.hour = 12;
        } else if (numValue < 1 && value.length > 0) {
          // For "from time", enforce minimum
          if (isFromTime) {
            const minTime = getMinTime();
            newTime.hour = minTime.hour;
            newTime.minute = minTime.minute;
            newTime.ampm = minTime.ampm;
          } else {
            newTime.hour = 1;
          }
        }
      }
    } else if (field === "minute") {
      if (value === "") {
        newTime.minute = "";
        setTime(newTime);
        return;
      }
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        if (numValue >= 0 && numValue <= 59) {
          newTime.minute = numValue;
          
          // Validate against minimum for "from time" only
          if (isFromTime && isTimeBelowMinimum(newTime)) {
            const minTime = getMinTime();
            newTime.hour = minTime.hour;
            newTime.minute = minTime.minute;
            newTime.ampm = minTime.ampm;
          }
        } else if (numValue > 59) {
          newTime.minute = 59;
        } else if (numValue < 0) {
          newTime.minute = 0;
        }
      }
    } else if (field === "ampm") {
      const upperValue = value.toUpperCase();
      if (upperValue === "A" || upperValue === "AM") {
        newTime.ampm = "AM";
      } else if (upperValue === "P" || upperValue === "PM") {
        newTime.ampm = "PM";
      } else {
        return;
      }
      
      // Validate against minimum for "from time"
      if (isFromTime && isTimeBelowMinimum(newTime)) {
        const minTime = getMinTime();
        newTime.hour = minTime.hour;
        newTime.minute = minTime.minute;
        newTime.ampm = minTime.ampm;
      }
    }
    
    setTime(newTime);
  };

  // Handle blur to validate and format
  const handleTimeBlur = (time, field, setTime, isFromTime = false) => {
    const newTime = { ...time };
    
    if (field === "hour") {
      if (newTime.hour === "" || newTime.hour < 1 || newTime.hour > 12) {
        // For "from time", use minimum hour
        if (isFromTime) {
          const minTime = getMinTime();
          newTime.hour = minTime.hour;
          newTime.minute = minTime.minute;
          newTime.ampm = minTime.ampm;
        } else {
          newTime.hour = 10; // Default
        }
      } else if (isFromTime && isTimeBelowMinimum(newTime)) {
        // Enforce minimum for "from time"
        const minTime = getMinTime();
        newTime.hour = minTime.hour;
        newTime.minute = minTime.minute;
        newTime.ampm = minTime.ampm;
      }
    } else if (field === "minute") {
      if (newTime.minute === "" || newTime.minute < 0 || newTime.minute > 59) {
        newTime.minute = 0; // Default
      }
      
      // Validate against minimum for "from time" only
      if (isFromTime && isTimeBelowMinimum(newTime)) {
        const minTime = getMinTime();
        newTime.hour = minTime.hour;
        newTime.minute = minTime.minute;
        newTime.ampm = minTime.ampm;
      }
    }
    
    setTime(newTime);
  };

  // Render time picker component
  const renderTimePicker = (time, setTime, isFromTime = false) => {
    // Check if hour down arrow should be disabled for "from time" only
    const isHourDownDisabled = isFromTime && (() => {
      const minTime = getMinTime();
      const minHour = minTime.hour;
      const minAMPM = minTime.ampm;
      
      // Convert to 24-hour format for comparison
      const timeHour = typeof time.hour === "number" ? time.hour : parseInt(String(time.hour), 10);
      let currentHour24 = timeHour;
      if (time.ampm === "PM") {
        if (timeHour === 12) {
          currentHour24 = 12;
        } else {
          currentHour24 = timeHour + 12;
        }
      } else {
        if (timeHour === 12) {
          currentHour24 = 0;
        } else {
          currentHour24 = timeHour;
        }
      }
      
      let minHour24 = minHour;
      if (minAMPM === "PM") {
        if (minHour === 12) {
          minHour24 = 12;
        } else {
          minHour24 = minHour + 12;
        }
      } else {
        if (minHour === 12) {
          minHour24 = 0;
        } else {
          minHour24 = minHour;
        }
      }
      
      // Disable if at or below minimum (same hour and same AM/PM, or below)
      if (currentHour24 < minHour24) {
        return true;
      } else if (currentHour24 === minHour24 && time.ampm === minAMPM) {
        return true;
      }
      return false;
    })();
    
    return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flex: 1,
    }}>
        {/* Hours */}
        <div style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}>
          <input
            type="text"
            value={time.hour || ""}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, ""); // Only numbers
              handleTimeInputChange(time, "hour", value, setTime, isFromTime);
            }}
            onBlur={() => handleTimeBlur(time, "hour", setTime, isFromTime)}
            style={{
              width: "70px",
              height: "34px",
              border: "1px solid #E9ECEF",
              borderRadius: "8px",
              backgroundColor: "#F8F9FA",
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: 400,
              color: "#495057",
              paddingRight: "28px",
              paddingLeft: "8px",
              boxSizing: "border-box",
              textAlign: "center",
              outline: "none",
            }}
          />
          <div style={{
            position: "absolute",
            right: "6px",
            display: "flex",
            flexDirection: "column",
            gap: "0px",
          }}>
            <button
              type="button"
              onClick={() => adjustTime(time, "hour", 1, setTime, isFromTime)}
              onMouseDown={(e) => e.preventDefault()}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "0px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "16px",
                height: "12px",
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 2L7.5 5H2.5L5 2Z" fill="#495057" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => adjustTime(time, "hour", -1, setTime, isFromTime)}
              disabled={isHourDownDisabled}
              onMouseDown={(e) => e.preventDefault()}
              style={{
                border: "none",
                background: "transparent",
                cursor: isHourDownDisabled ? "not-allowed" : "pointer",
                opacity: isHourDownDisabled ? 0.5 : 1,
                padding: "0px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "16px",
                height: "12px",
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 8L2.5 5H7.5L5 8Z" fill={isHourDownDisabled ? "#ADB5BD" : "#495057"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Colon */}
        <span style={{
          fontFamily: "Inter, sans-serif",
          fontSize: "14px",
          fontWeight: 500,
          color: "#495057",
          margin: "0 2px",
        }}>
          :
        </span>

        {/* Minutes */}
        <div style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}>
          <input
            type="text"
            value={time.minute === "" || time.minute === undefined || time.minute === null ? "" : (typeof time.minute === "number" ? time.minute.toString().padStart(2, "0") : time.minute)}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, ""); // Only numbers
              handleTimeInputChange(time, "minute", value, setTime, isFromTime);
            }}
            onBlur={() => handleTimeBlur(time, "minute", setTime, isFromTime)}
            style={{
              width: "70px",
              height: "34px",
              border: "1px solid #E9ECEF",
              borderRadius: "8px",
              backgroundColor: "#F8F9FA",
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: 400,
              color: "#495057",
              paddingRight: "28px",
              paddingLeft: "8px",
              boxSizing: "border-box",
              textAlign: "center",
              outline: "none",
            }}
          />
          <div style={{
            position: "absolute",
            right: "6px",
            display: "flex",
            flexDirection: "column",
            gap: "0px",
          }}>
            <button
              type="button"
              onClick={() => adjustTime(time, "minute", 1, setTime)}
              onMouseDown={(e) => e.preventDefault()}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "0px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "16px",
                height: "12px",
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 2L7.5 5H2.5L5 2Z" fill="#495057" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => adjustTime(time, "minute", -1, setTime)}
              onMouseDown={(e) => e.preventDefault()}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "0px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "16px",
                height: "12px",
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 8L2.5 5H7.5L5 8Z" fill="#495057" />
              </svg>
            </button>
          </div>
        </div>

        {/* AM/PM */}
        <div style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}>
          <input
            type="text"
            value={time.ampm}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              if (value === "A" || value === "AM" || value === "P" || value === "PM") {
                handleTimeInputChange(time, "ampm", value, setTime);
              } else if (value === "") {
                // Allow clearing
                return;
              }
            }}
            onBlur={() => {
              const newTime = { ...time };
              if (newTime.ampm !== "AM" && newTime.ampm !== "PM") {
                newTime.ampm = "AM"; // Default
              }
              setTime(newTime);
            }}
            style={{
              width: "70px",
              height: "34px",
              border: "1px solid #E9ECEF",
              borderRadius: "8px",
              backgroundColor: "#F8F9FA",
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: 400,
              color: "#495057",
              paddingRight: "28px",
              paddingLeft: "8px",
              boxSizing: "border-box",
              textAlign: "center",
              outline: "none",
              textTransform: "uppercase",
            }}
          />
          <div style={{
            position: "absolute",
            right: "6px",
            display: "flex",
            flexDirection: "column",
            gap: "0px",
          }}>
            <button
              type="button"
              onClick={() => adjustTime(time, "ampm", 1, setTime)}
              onMouseDown={(e) => e.preventDefault()}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "0px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "16px",
                height: "12px",
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 2L7.5 5H2.5L5 2Z" fill="#495057" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => adjustTime(time, "ampm", -1, setTime)}
              onMouseDown={(e) => e.preventDefault()}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "0px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "16px",
                height: "12px",
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 8L2.5 5H7.5L5 8Z" fill="#495057" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to combine date and time into ISO format
  const combineDateTime = (dateMoment, time) => {
    if (!dateMoment || !time || time.hour === "" || time.minute === "" || 
        time.hour === undefined || time.minute === undefined) {
      return null;
    }

    const hour = typeof time.hour === "number" ? time.hour : parseInt(time.hour, 10) || 0;
    const minute = typeof time.minute === "number" ? time.minute : parseInt(time.minute, 10) || 0;
    const ampm = time.ampm || "AM";

    // Convert to 24-hour format
    let hour24 = hour;
    if (ampm === "PM" && hour !== 12) {
      hour24 = hour + 12;
    } else if (ampm === "AM" && hour === 12) {
      hour24 = 0;
    }

    // Create a new moment with the date and time
    const dateTime = moment(dateMoment)
      .hour(hour24)
      .minute(minute)
      .second(0)
      .millisecond(0);

    // Return ISO string
    return dateTime.toISOString();
  };

  const handleCreateShipment = () => {
    if (!boxType || devices.length === 0 || isSubmitting) return;
    const storeId = user?.store?.id;
    if (!storeId) {
      setErrorMessage("Please select a store.");
      return;
    }

    const deviceIds = devices.map((device) => device.id).filter(Boolean);
    if (deviceIds.length === 0) {
      setErrorMessage("Selected devices are missing IDs.");
      return;
    }

    // Pickup date and time are now optional
    let pickupMinDateTime = null;
    let pickupMaxDateTime = null;

    // Only validate and combine date/time if they are provided
    if (pickupRange && pickupRange.length === 2 && fromTime && toTime) {
      const hasFromTime = fromTime.hour !== "" && fromTime.minute !== "" && 
                         fromTime.hour !== undefined && fromTime.minute !== undefined;
      const hasToTime = toTime.hour !== "" && toTime.minute !== "" && 
                       toTime.hour !== undefined && toTime.minute !== undefined;

      if (hasFromTime && hasToTime) {
        pickupMinDateTime = combineDateTime(pickupRange[0], fromTime);
        pickupMaxDateTime = combineDateTime(pickupRange[1], toTime);

        if (!pickupMinDateTime || !pickupMaxDateTime) {
          setErrorMessage("Invalid pickup date or time. Please try again.");
          return;
        }
        
        // Validate that toDateTime is at least 24 hours after fromDateTime (only if both are provided)
        const fromMoment = moment(pickupMinDateTime);
        const toMoment = moment(pickupMaxDateTime);
        const diffInHours = toMoment.diff(fromMoment, "hours", true);
        
        // Check if difference is less than 24 hours
        if (diffInHours < 24) {
          setErrorMessage("End date and time must be at least 24 hours after start date and time.");
          setIsSubmitting(false);
          return;
        }
      }
    }

    setIsSubmitting(true);
    setErrorMessage("");

    // Build request body - only include pickup dates if they are provided
    const requestBody = {
      devices: deviceIds,
      boxTypeId: boxType,
      instructions: instructions.trim() || "",
    };

    // Only add pickup dates if they are provided
    if (pickupMinDateTime && pickupMaxDateTime) {
      requestBody.pickupMinDateTime = pickupMinDateTime;
      requestBody.pickupMaxDateTime = pickupMaxDateTime;
    }

    request({
      path: "/shipment",
      method: "POST",
      headers: { "X-Store-Id": storeId },
      body: requestBody,
    })
      .then((response) => {
        const pdfUrl = response?.inboundShippingLabelUrl;
        if (pdfUrl) {
          window.open(pdfUrl, "_blank", "noopener,noreferrer");
        }
        showSuccessToast(SUCCESS_TOAST_MESSAGES.shipmentCreated);
        if (onCreateShipment) {
          onCreateShipment({ boxType, devices, response });
        }
        handleClose();
      })
      .catch((error) => {
        let message =
          error?.message || "Failed to create shipment. Please try again.";
        try {
          const parsed = JSON.parse(message);
          if (parsed?.message) {
            message = parsed.message;
          }
        } catch (parseError) {
          // ignore parse errors
        }
        setErrorMessage(message);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleRemoveDevice = (index) => {
    const newDevices = devices.filter((_, i) => i !== index);
    setDevices(newDevices);
  };

  const tableColumns = [
    { key: "tradeInId", label: "Trade-In ID", sortable: true },
    { key: "modelId", label: "Model ID", sortable: true },
    { key: "action", label: "Action", sortable: true },
  ];

  const formatPickupSchedule = () => {
    if (!pickupRange || pickupRange.length !== 2) {
      return {
        text: `${moment().format("MMMM D")} - Set date`,
        dateText: "",
        timeText: "",
      };
    }
    const [start, end] = pickupRange;
    
    // Format time from state
    const formatTime = (time) => {
      if (!time || time.hour === "" || time.minute === "" || time.hour === undefined || time.minute === undefined) {
        return "";
      }
      const hour = typeof time.hour === "number" ? time.hour : parseInt(time.hour, 10) || 10;
      const minute = typeof time.minute === "number" ? time.minute : parseInt(time.minute, 10) || 0;
      const minuteStr = minute.toString().padStart(2, "0");
      const ampm = time.ampm || "AM";
      return `${hour}:${minuteStr} ${ampm}`;
    };
    
    return {
      dateText: `${start.format("MMMM D")} to ${end.format("MMMM D")}`,
      timeText: `${formatTime(fromTime)} to ${formatTime(toTime)}`,
    };
  };

  // Calculate minimum "to date" - 24 hours after "from date" + "from time"
  const getMinToDate = (fromDate) => {
    if (!fromDate || !fromTime || fromTime.hour === "" || fromTime.minute === "" || 
        fromTime.hour === undefined || fromTime.minute === undefined) {
      return null;
    }
    
    // Convert fromTime to 24-hour format
    let hour24 = fromTime.hour;
    if (fromTime.ampm === "PM" && fromTime.hour !== 12) {
      hour24 = fromTime.hour + 12;
    } else if (fromTime.ampm === "AM" && fromTime.hour === 12) {
      hour24 = 0;
    }
    
    // Create moment with from date and from time
    const fromDateTime = moment(fromDate)
      .hour(hour24)
      .minute(fromTime.minute)
      .second(0)
      .millisecond(0);
    
    // Add 24 hours
    const minToDateTime = moment(fromDateTime).add(24, "hours");
    
    // Return the date part (time will be handled separately)
    return minToDateTime;
  };

  // Disable dates that are less than 24 hours after the "from date" + "from time"
  const disabledDate = (current, type) => {
    if (!pickupRange || pickupRange.length === 0) return false;
    
    if (type === "end") {
      const fromDate = pickupRange[0];
      if (!fromDate) return false;
      
      const minToDateTime = getMinToDate(fromDate);
      if (!minToDateTime) return false;
      
      // Disable dates before the minimum date
      return current && current.isBefore(minToDateTime, "day");
    }
    
    return false;
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      aria-labelledby="create-shipment-title"
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography className={classes.title} id="create-shipment-title">
          Create Shipment
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          aria-label="close"
        >
          <svg
            className={classes.closeIcon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.formContainer}>
          {boxInventoryWarning && (
            <div className={classes.warningAlert}>
              <svg
                className={classes.warningIcon}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm1 15h-2v-2h2v2Zm0-4h-2V7h2v6Z"
                  fill="currentColor"
                />
              </svg>
              <Typography className={classes.warningText}>
                {boxInventoryWarning}
              </Typography>
          </div>
          )}
          {errorMessage && (
            <div className={classes.errorAlert}>
              <svg
                className={classes.errorIcon}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm0 13.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-1-8h2v6h-2V7.5Z"
                  fill="currentColor"
                />
              </svg>
              <Typography className={classes.errorText}>
                {errorMessage}
              </Typography>
            </div>
          )}
          <div className={classes.section}>
            <label className={classes.label}>Selected Devices</label>
            <div className={classes.tableContainer}>
              <Table className={classes.table}>
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    {tableColumns.map((column) => (
                      <TableCell key={column.key}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {column.label}
                          {column.sortable && (
                            <svg
                              className={classes.sortIcon}
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7 10L12 15L17 10H7Z"
                                fill="currentColor"
                              />
                            </svg>
                          )}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.length > 0 ? (
                    devices.map((device, index) => (
                      <TableRow key={index} className={classes.tableRow}>
                        <TableCell>{device.tradeInId || "—"}</TableCell>
                        <TableCell>{device.modelId || "—"}</TableCell>
                        <TableCell>
                          <span
                            className={classes.removeLink}
                            onClick={() => handleRemoveDevice(index)}
                          >
                            Remove
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow className={classes.tableRow}>
                      <TableCell colSpan={3} style={{ textAlign: "center" }}>
                        No devices selected
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          {/* Temporarily hidden - Pick-up Details */}
          {false && (
            <>
              <Typography className={classes.pickupSectionTitle}>
                Pick-up Details
              </Typography>
              <div className={classes.section}>
                <div className={classes.pickupLabel}>Schedule</div>
            <div
              className={classes.pickupPickerWrapper}
              onClick={() => {
                setIsPickerOpen(true);
                setPickerErrorMessage(""); // Clear error when opening picker
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsPickerOpen(true);
                  setPickerErrorMessage(""); // Clear error when opening picker
                }
              }}
            >
              <div className={classes.pickupDisplay}>
                {pickupRange && pickupRange.length === 2 ? (
                  <>
                    <span className={classes.pickupMeta}>
                      {formatPickupSchedule().dateText.split(" to ")[0]}
                      <span className={classes.pickupMuted}>to</span>
                      {formatPickupSchedule().dateText.split(" to ")[1]}
                    </span>
                    <span className={classes.pickupDivider}>|</span>
                    <span className={classes.pickupMeta}>
                      {formatPickupSchedule().timeText.split(" to ")[0]}
                      <span className={classes.pickupMuted}>to</span>
                      {formatPickupSchedule().timeText.split(" to ")[1]}
                    </span>
                  </>
                ) : (
                  <span>{formatPickupSchedule().text}</span>
                )}
                <svg
                  className={classes.pickupIcon}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 4H5C3.89 4 3 4.9 3 6V20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM7 11H9V13H7V11ZM11 11H13V13H11V11ZM15 11H17V13H15V11ZM7 15H9V17H7V15ZM11 15H13V17H11V15ZM15 15H17V17H15V15Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <DatePicker.RangePicker
                className={classes.pickupPicker}
                style={{ width: "100%", height: "100%" }}
                inputReadOnly
                value={pickupRange}
                onChange={(values) => {
                  if (!values || values.length === 0) {
                    setPickupRange([]);
                    return;
                  }
                  
                  if (values.length === 1) {
                    // Only start date selected
                    setPickupRange(values);
                    if (values[0]) {
                      const fromDate = values[0];
                      setPickerMonth(moment(fromDate));
                    }
                    return;
                  }
                  
                  if (values.length === 2) {
                    const [start, end] = values;
                    if (!start || !end) {
                      setPickupRange(values);
                      return;
                    }
                    
                    const minToDateTime = getMinToDate(start);
                    
                    // If end date is before minimum, adjust it to minimum date
                    if (minToDateTime && end.isBefore(minToDateTime, "day")) {
                      setPickupRange([start, moment(minToDateTime).startOf("day")]);
                    } else {
                      setPickupRange(values);
                    }
                    
                    // Update picker month based on start date
                    if (start) {
                      setPickerMonth(moment(start));
                    }
                  }
                }}
                onPanelChange={(values, mode) => {
                  // Update picker month when user navigates
                  if (values && values.length > 0) {
                    setPickerMonth(moment(values[0]));
                  }
                }}
                pickerValue={(() => {
                  // Always show the same month for both calendars
                  if (pickupRange && pickupRange.length > 0 && pickupRange[0]) {
                    const fromDate = pickupRange[0];
                    const fromMonth = moment(fromDate);
                    // Both panels show the same month
                    return [fromMonth, fromMonth];
                  }
                  // Default: current month for both
                  return [pickerMonth, pickerMonth];
                })()}
                onPickerValueChange={(values) => {
                  // Keep both calendars synchronized
                  if (values && values.length > 0) {
                    setPickerMonth(moment(values[0]));
                  }
                }}
                picker="date"
                format="MMMM D"
                separator=" to "
                suffixIcon={null}
                popupClassName="pickup-range-picker"
                getPopupContainer={() => document.body}
                open={isPickerOpen}
                onOpenChange={(open) => {
                  // Only allow closing via Save button, not by clicking dates
                  if (!open) {
                    if (allowCloseRef.current) {
                      // Reset the flag after closing
                      allowCloseRef.current = false;
                      setIsPickerOpen(false);
                    }
                    // If not allowed to close, don't update state
                    return;
                  }
                  // Allow opening
                  setIsPickerOpen(true);
                }}
                disabledDate={disabledDate}
                renderExtraFooter={() => (
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    padding: "20px",
                    borderTop: "1px solid #F1F3F5",
                    backgroundColor: "#FFFFFF",
                  }}>
                    <div style={{
                      display: "flex",
                      gap: "24px",
                      justifyContent: "center",
                    }}>
                      {renderTimePicker(fromTime, setFromTime, true)}
                      {renderTimePicker(toTime, setToTime, false)}
                    </div>
                    <div style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      paddingTop: "8px",
                    }}>
                      {pickerErrorMessage && (
                        <div style={{
                          color: "#DC3545",
                          fontSize: "11px",
                          fontFamily: "Inter, sans-serif",
                          marginBottom: "8px",
                          textAlign: "center",
                        }}>
                          {pickerErrorMessage}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          // Validate from time against minimum
                          // Check if time fields are empty or invalid
                          if (!fromTime || fromTime.hour === "" || fromTime.minute === "" || 
                              fromTime.hour === undefined || fromTime.minute === undefined ||
                              fromTime.ampm === undefined || fromTime.ampm === "") {
                            setPickerErrorMessage("Please select a valid time.");
                            return; // Don't close the popup
                          }
                          
                          // Check if time is below minimum
                          const isBelowMin = isTimeBelowMinimum(fromTime);
                          const minTime = getMinTime();
                          console.log("Time validation:", {
                            fromTime,
                            minTime,
                            isBelowMin,
                            timeHour24: (() => {
                              const timeHour = typeof fromTime.hour === "number" ? fromTime.hour : parseInt(String(fromTime.hour), 10);
                              let timeHour24 = timeHour;
                              if (fromTime.ampm === "PM") {
                                if (timeHour === 12) {
                                  timeHour24 = 12;
                                } else {
                                  timeHour24 = timeHour + 12;
                                }
                              } else {
                                if (timeHour === 12) {
                                  timeHour24 = 0;
                                } else {
                                  timeHour24 = timeHour;
                                }
                              }
                              return timeHour24;
                            })(),
                            minHour24: (() => {
                              let minHour24 = minTime.hour;
                              if (minTime.ampm === "PM") {
                                if (minTime.hour === 12) {
                                  minHour24 = 12;
                                } else {
                                  minHour24 = minTime.hour + 12;
                                }
                              } else {
                                if (minTime.hour === 12) {
                                  minHour24 = 0;
                                } else {
                                  minHour24 = minTime.hour;
                                }
                              }
                              return minHour24;
                            })()
                          });
                          if (isBelowMin) {
                            const minTimeStr = `${minTime.hour}:${minTime.minute.toString().padStart(2, "0")} ${minTime.ampm}`;
                            setPickerErrorMessage(`Cannot select time below minimum (${minTimeStr}). Please select a valid time.`);
                            return; // Don't close the popup
                          }
                          
                          // Validate to time - check if date and time together is at least 24 hours after from date and time
                          if (!toTime || toTime.hour === "" || toTime.minute === "" || 
                              toTime.hour === undefined || toTime.minute === undefined ||
                              toTime.ampm === undefined || toTime.ampm === "") {
                            setPickerErrorMessage("Please select a valid end time.");
                            return; // Don't close the popup
                          }
                          
                          // Only validate if toDate is the next day (tomorrow) or later
                          // If same day, no validation needed (can't be 24 hours anyway)
                          if (pickupRange && pickupRange.length >= 2 && pickupRange[0] && pickupRange[1]) {
                            const fromDate = moment(pickupRange[0]);
                            const toDate = moment(pickupRange[1]);
                            
                            // Check if toDate is tomorrow or later (different day)
                            if (toDate.isAfter(fromDate, "day")) {
                              // Combine date and time for both from and to
                              const fromDateTime = combineDateTime(pickupRange[0], fromTime);
                              const toDateTime = combineDateTime(pickupRange[1], toTime);
                              
                              if (fromDateTime && toDateTime) {
                                const fromMoment = moment(fromDateTime);
                                const toMoment = moment(toDateTime);
                                const diffInHours = toMoment.diff(fromMoment, "hours", true);
                                
                                console.log("To time validation (full datetime):", {
                                  fromDateTime: fromMoment.format("YYYY-MM-DD HH:mm:ss"),
                                  toDateTime: toMoment.format("YYYY-MM-DD HH:mm:ss"),
                                  diffInHours,
                                  isBelow24Hours: diffInHours < 24
                                });
                                
                                if (diffInHours < 24) {
                                  setPickerErrorMessage("End date and time must be at least 24 hours after start date and time. Please select a valid time.");
                                  return; // Don't close the popup
                                }
                              }
                            }
                            // If same day, no validation needed
                          }
                          
                          // Clear any previous error
                          setPickerErrorMessage("");
                          allowCloseRef.current = true;
                          // Use setTimeout to ensure ref is set before state update triggers onOpenChange
                          setTimeout(() => {
                            setIsPickerOpen(false);
                          }, 0);
                        }}
                        style={{
                          fontFamily: "Inter, sans-serif",
                          height: 40,
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#FFFFFF",
                          backgroundColor: "#7650E1",
                          border: "none",
                          borderRadius: "6px",
                          width: 69,
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#5A32A3";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#7650E1";
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              />
              </div>
              </div>
              {/* Temporarily hidden - Instructions */}
              <div className={classes.section}>
                <label className={classes.instructionsLabel}>Instructions</label>
                <TextField
                  className={classes.instructionsInput}
                  placeholder="Enter text here"
                  variant="outlined"
                  multiline
                  rows={3}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  fullWidth
                />
              </div>
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button className={classes.cancelButton} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className={classes.createButton}
          onClick={handleCreateShipment}
          variant="contained"
          disabled={!boxType || devices.length === 0 || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              Creating...
            </>
          ) : (
            "Create & Print"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateShipmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateShipment: PropTypes.func.isRequired,
  selectedDevices: PropTypes.arrayOf(
    PropTypes.shape({
      modelId: PropTypes.string,
      model: PropTypes.string,
      tradeInId: PropTypes.string,
    })
  ),
};

CreateShipmentModal.defaultProps = {
  selectedDevices: [],
  boxInventory: [],
};

export default CreateShipmentModal;

